import React, { useState, useMemo, useCallback } from 'react';
import { Nav, Modal, CloseButton, Button, Form, InputGroup, Spinner, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart as cart, faTrash, faSyncAlt as faRotateRight } from '@fortawesome/free-solid-svg-icons'
import { collection, query, setDoc, where, doc, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from 'config/firebase';
import { toast } from 'react-toastify';

const styleObject = {
  position: 'relative',
  top: '-2px',
  backgroundColor: '#bed0f1',
  borderRadius: '50%',
  color: '#eef1f9',
  width: '32px',
  height: '32px'
}

const OpenOrders = ({ user }) => {

  const [showModal, setShowModal] = useState(false);
  // Source-of-truth for the modal list is a fresh fetch on every open, not a
  // long-lived snapshot listener. Background tab suspension, auth refresh
  // failures, and dropped websockets can leave a listener returning stale or
  // empty data — that was making carts appear "lost" until users cleared cache.
  const [openOrders, setOpenOrders] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const openOrdersQuery = useMemo(
    () => query(
      collection(db, "openOrders"),
      where("uid", "==", user.uid),
      where("status", "==", 'Open')
    ),
    [user.uid]
  );

  const navigate = useNavigate();

  const refreshOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const snap = await getDocs(openOrdersQuery);
      const rows = snap.docs.map(d => ({ ...d.data(), docId: d.id }));
      setOpenOrders(rows);
    } catch (err) {
      console.error('OpenOrders fetch failed:', err);
      toast.error('Failed to load open orders. Try refreshing.', { theme: 'colored', toastId: 'open-orders-error' });
    } finally {
      setLoadingOrders(false);
    }
  }, [openOrdersQuery]);

  async function createNewManualOrder(batchName) {
    try {
      const docRef = doc(collection(db, "openOrders"));
      await setDoc(docRef, {
        docId: docRef.id,
        orderBatchName: batchName,
        uid: user.uid,
        status: "Open"
      });
      refreshOrders();
      navigate(`cart/${docRef.id}`);

    } catch (error) {
      console.log(error);
    }

  }

  function handleNewOrder(e){
    e.preventDefault();
    setShowModal(false);
    const input = document.getElementById('newBatchName').value;
    createNewManualOrder(input)
  }

  async function handleDeleteOrder(docId) {
    if (!docId) {
      toast.error("Cannot delete: order ID is missing", { theme: "colored" });
      return;
    }
    try {
      await deleteDoc(doc(db, "openOrders", docId));
      toast.success("Order deleted", { theme: "colored" });
    } catch (error) {
      console.log("Error deleting order:", error);
      toast.error("Failed to delete order", { theme: "colored" });
    }
  }

  async function handleModalOpen() {
    setShowModal(true);
    refreshOrders();
  }

  async function handleDeleteOrderAndRefresh(docId) {
    await handleDeleteOrder(docId);
    refreshOrders();
  }

  return (
    <>
      { user !== null
      ?
        <Nav.Item as="li" className="rounded" key={"openOrdersNavItem"}>

          <Nav.Link
            as={Link}
            onClick={handleModalOpen}
            className="pe-0 ps-2 nav-link"
            key="openOrdersNavItemLink"
          >

            <div style={styleObject} key={"openOrdersNavStyleObject"}>

              <FontAwesomeIcon
                key={"openOrdersNavStyleObjectIcon"}
                icon={cart}
                transform="shrink-7"
                className="fs-4"
                style={{marginLeft: '-3px'}}
                variant={"falcon-danger"}
              />

            </div>
          </Nav.Link>
        </Nav.Item>
      :
        null
      }

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        key={"openOrdersNavItemModal"}
      >
        <Modal.Header>
          <Modal.Title id="contained-modal-title-vcenter">Open Manual Orders</Modal.Title>
          <CloseButton
            className="btn btn-circle btn-sm transition-base p-0"
            onClick={() => setShowModal(false)}
          />
        </Modal.Header>
        <Modal.Body>

          <h5>Create New Order</h5>
          <Form onSubmit={(e) => handleNewOrder(e)} key={"newOrderFormKey"}>
            <InputGroup className="mb-3">
              <Form.Control
                placeholder="Order Or Batch Name"
                aria-label="Order Or Batch Name"
                aria-describedby="basic-addon2"
                id="newBatchName"
                key={"newBatchNameInputKey"}
              />
              <Button type={"submit"} variant="falcon-success" id="button-addon2">
                Start New Order
              </Button>
            </InputGroup>
          </Form>

          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Continue an Existing Order</h5>
            <Button
              variant="falcon-default"
              size="sm"
              onClick={refreshOrders}
              disabled={loadingOrders}
              title="Refresh list"
            >
              <FontAwesomeIcon icon={faRotateRight} spin={loadingOrders} />
            </Button>
          </div>

          {loadingOrders && openOrders === null ? (
              <Spinner />
            ) : openOrders && openOrders.length > 0 ? (
              openOrders.map((order, idx) => {
                if (!order || !order.docId) return null;
                if (order.status === 'Submitted') return null;
                return (
                  <Row key={order.docId} className="align-items-center mb-1">
                    <Col>
                      <Link to={`cart/${order.docId}`} onClick={() => setShowModal(false)}>
                        {order.orderBatchName || '(unnamed)'}
                      </Link>
                    </Col>
                    <Col xs="auto">
                      <Button
                        variant="falcon-danger"
                        size="sm"
                        className="p-1"
                        onClick={() => handleDeleteOrderAndRefresh(order.docId)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </Col>
                  </Row>
                );
              })
            ) : (
              <p className="text-muted mb-0 mt-2">No open orders.</p>
            )}

        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>

  );
};

export default OpenOrders;
